import { atom } from "jotai";

import {
  SAMPLE_IMAGE_URLS,
  sampleRandomImages,
  type GeneratedImage
} from "../ui/components/AiImageGallery";
import type { RefinementHistoryEntry } from "./useRefinementHistory";

// Menu Constants and Types
export const MENU_IDS = {
  AI_DESIGN: "ai-design",
  UPLOAD_IMPORT: "upload-import",
  VECTOR_ARTS: "vector-arts",
  LIBRARY: "library",
  COMMUNITY_ART: "community-art"
} as const;

export type MenuId = (typeof MENU_IDS)[keyof typeof MENU_IDS];

// AI Design State Types and Atom
export type AiDesignSideState = {
  prompt: string;
  images: GeneratedImage[];
  isGenerating: boolean;
  uploadingImageId: number | undefined;
  error: string | undefined;
  startTime: number | undefined;
  isRefining: boolean;
  refinementImageId: number | undefined;
  refinementPrompt: string;
  refinementError: string | undefined;
  refinementHistory: RefinementHistoryEntry[];
  refinementHistoryIndex: number;
};

export type AiDesignState = {
  front: AiDesignSideState;
  back: AiDesignSideState;
};

export const aiDesignStateAtom = atom<AiDesignState>({
  front: {
    prompt: "",
    images: sampleRandomImages(SAMPLE_IMAGE_URLS),
    isGenerating: false,
    uploadingImageId: undefined,
    error: undefined,
    startTime: undefined,
    isRefining: false,
    refinementImageId: undefined,
    refinementPrompt: "",
    refinementError: undefined,
    refinementHistory: [],
    refinementHistoryIndex: -1
  },
  back: {
    prompt: "",
    images: sampleRandomImages(SAMPLE_IMAGE_URLS),
    isGenerating: false,
    uploadingImageId: undefined,
    error: undefined,
    startTime: undefined,
    isRefining: false,
    refinementImageId: undefined,
    refinementPrompt: "",
    refinementError: undefined,
    refinementHistory: [],
    refinementHistoryIndex: -1
  }
});

// Library State Types and Atom
export type LibraryState = {
  front: {
    search: string;
    sort: string;
  };
  back: {
    search: string;
    sort: string;
  };
};

export const libraryStateAtom = atom<LibraryState>({
  front: { search: "", sort: "newest" },
  back: { search: "", sort: "newest" }
});

// Artwork Selection State Types and Atom
export type ArtworkSelectionState = {
  front: {
    activeSource: MenuId | undefined;
    selectedId: number | undefined;
  };
  back: {
    activeSource: MenuId | undefined;
    selectedId: number | undefined;
  };
};

export const artworkSelectionAtom = atom<ArtworkSelectionState>({
  front: {
    activeSource: undefined,
    selectedId: undefined
  },
  back: {
    activeSource: undefined,
    selectedId: undefined
  }
});

// Menu State Atom
export const activeMenuAtom = atom<MenuId | undefined>(undefined);

// Token Management Atom
export const aiCreditsBalanceAtom = atom<number | undefined>(undefined);
